import React, { useEffect, useState } from 'react';
import apiCall from '../helper/apiCall';

const OrderDetailsModal = ({ orderId, show, onClose,shippingStatuses }) => {
    const [orderDetails, setOrderDetails] = useState(null);
    const [editedSubOrders, setEditedSubOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState(null);

    useEffect(() => {
        // Only fetch data if the modal is visible and an orderId is provided
        if (show && orderId) {
            const fetchOrderDetails = async () => {
                setLoading(true);
                setError(null);
                const payload ={order_id: orderId};
                const response = await apiCall("getOrderDetail", payload);
                
                if (response?.success) {
                    if(response.data){
                        setOrderDetails(response.data || []);
                        setEditedSubOrders(response.data.shipments.map(subOrder => ({
                        ...subOrder,
                        order_id: subOrder.order_id,
                        shipping_date: subOrder.shipping_date || '',
                        shipping_tracking_id: subOrder.shipping_tracking_id || '',
                        shipping_status_id: subOrder.shipping_status_id,
                        shipping_cost: subOrder.shipping_cost || '0.00' // Initialize the new field
                    })));
                    }
                    
                } else {
                    setError(response?.message || "An unknown error occurred.");
                }
                setLoading(false);
            };
            fetchOrderDetails();
        }
    }, [show, orderId]);

    // Do not render if the modal is not visible
    if (!show) {
        return null;
    }

    const handleSubOrderChange = (subOrderId, field, value) => {
        setEditedSubOrders(prev => prev.map(subOrder => 
            subOrder.sub_order_id === subOrderId 
                ? { ...subOrder, [field]: value } 
                : subOrder
        ));
    };

    const handleSaveSubOrder = async (subOrderId) => {
        setIsSaving(true);
        setSaveMessage(null);
        const subOrderToUpdate = editedSubOrders.find(so => so.sub_order_id === subOrderId);

        if (subOrderToUpdate) {
            const response = await apiCall("updateShippingDetail", {
                subOrderId,
                sub_order_id: subOrderId,
                order_id: orderId,
                shipping_date: subOrderToUpdate.shipping_date,
                shipping_tracking_id: subOrderToUpdate.shipping_tracking_id,
                shipping_status_name: subOrderToUpdate.shipping_status_name,
                shipping_status_id: subOrderToUpdate.shipping_status_id,
                shipping_cost: subOrderToUpdate.shipping_cost // Add the new field to the payload
            });

            if (response.success) {
                setSaveMessage({ type: 'success', text: `Sub-order #${subOrderId} updated successfully!` });
            } else {
                setSaveMessage({ type: 'error', text: `Failed to update sub-order #${subOrderId}.` });
            }
        }
        setIsSaving(false);
        setTimeout(() => setSaveMessage(null), 3000); // Clear message after 3 seconds
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className="flex justify-center items-center h-full py-10">
                    <p className="text-gray-500 text-lg">Loading order details...</p>
                </div>
            );
        }

        if (error) {
            return (
                <div className="flex justify-center items-center h-full py-10">
                    <p className="text-red-500 text-lg">Error: {error}</p>
                </div>
            );
        }

        if (!orderDetails) {
            return (
                <div className="flex justify-center items-center h-full py-10">
                    <p className="text-gray-500 text-lg">No order details found.</p>
                </div>
            );
        }

        const { shippingAddress, billingAddress } = orderDetails;

        return (
            <div className="space-y-8">
                {saveMessage && (
                    <div className={`p-4 rounded-lg text-center ${saveMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {saveMessage.text}
                    </div>
                )}
                {/* Section 1: Addresses */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Shipping Address */}
                    <div className="p-5 border border-gray-200 rounded-lg shadow-sm bg-gray-50">
                        <h3 className="text-xl font-medium text-gray-700 mb-4">Shipping Address</h3>
                        <p><strong>Name:</strong> {shippingAddress.shipping_full_name}</p>
                        <p><strong>Mobile:</strong> {shippingAddress.shipping_mobile_no}</p>
                        <p><strong>Address:</strong> {shippingAddress.shipping_address_line1}, {shippingAddress.shipping_address_line2}</p>
                        <p><strong>Landmark:</strong> {shippingAddress.shipping_land_mark || 'N/A'}</p>
                        <p><strong>Pincode:</strong> {shippingAddress.shipping_pincode}</p>
                        <p><strong>Location:</strong> {shippingAddress.shipping_city}, {shippingAddress.shipping_state_name}, {shippingAddress.shipping_country_name}</p>
                    </div>
                    {/* Billing Address */}
                    <div className="p-5 border border-gray-200 rounded-lg shadow-sm bg-gray-50">
                        <h3 className="text-xl font-medium text-gray-700 mb-4">Billing Address</h3>
                        <p><strong>Name:</strong> {billingAddress.billing_full_name}</p>
                        <p><strong>Mobile:</strong> {billingAddress.billing_mobile_no}</p>
                        <p><strong>Address:</strong> {billingAddress.billing_address_line1}, {billingAddress.billing_address_line2}</p>
                        <p><strong>Landmark:</strong> {billingAddress.billing_land_mark || 'N/A'}</p>
                        <p><strong>Pincode:</strong> {billingAddress.billing_pincode}</p>
                        <p><strong>Location:</strong> {billingAddress.billing_city}, {billingAddress.billing_state_name}, {billingAddress.billing_country_name}</p>
                    </div>
                </div>

                {/* Section 2: Sub Orders */}
                <div>
                    <h3 className="text-xl font-medium text-gray-700 mb-4">Sub Orders</h3>
                    {editedSubOrders.map(subOrder => (
                        <div key={subOrder.sub_order_id} className="mb-6 p-4 border border-gray-200 rounded-lg shadow-sm">
                            <h4 className="text-lg font-medium text-gray-600 mb-3">Sub Order #{subOrder.sub_order_id}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Shipping Date</label>
                                    <input 
                                        type="date"
                                        value={subOrder.shipping_date}
                                        max={new Date().toISOString().split('T')[0]}
                                        onChange={(e) => handleSubOrderChange(subOrder.sub_order_id, 'shipping_date', e.target.value)}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Shipping Number</label>
                                    <input 
                                        type="text"
                                        value={subOrder.shipping_tracking_id}
                                        onChange={(e) => handleSubOrderChange(subOrder.sub_order_id, 'shipping_tracking_id', e.target.value)}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Shipping Status</label>
                                    <select 
                                        value={subOrder.shipping_status_id}
                                        onChange={(e) => handleSubOrderChange(subOrder.sub_order_id, 'shipping_status_id', e.target.value)}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                                    >
                                        <option value="">Select status</option>
                                        {shippingStatuses.map((el) => (
                                            <option key={el.shipping_status_id} value={el.shipping_status_id}>
                                            {el.shipping_status_name}
                                            </option>
                                        ))}

                                        
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Shipping Cost</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={subOrder.shipping_cost}
                                        onChange={(e) => handleSubOrderChange(subOrder.sub_order_id, 'shipping_cost', e.target.value)}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                                    />
                                </div>
                            </div>
                            <button
                                onClick={() => handleSaveSubOrder(subOrder.sub_order_id)}
                                disabled={isSaving}
                                className={`w-full py-2 px-4 rounded-md text-white font-semibold transition-colors ${isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                            <div className="space-y-4 mt-4">
                                {subOrder.items.map(item => (
                                    <div key={item.order_item_id} className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 border-b border-gray-100 last:border-b-0 pb-4">
                                        <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden bg-gray-100">
                                            <img 
                                                src={item.product_item_images[0]} 
                                                alt={item.product_name} 
                                                className="w-full h-full object-cover" 
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-semibold text-gray-800">{item.product_name}</p>
                                            <p className="text-sm text-gray-500">Color: {item.color_name} | Size: {item.size_name}</p>
                                            <p className="text-sm text-gray-500">Qty: {item.quantity} | Price: ₹{item.sell_price}</p>
                                            <p className="text-sm text-red-500">Discount: ₹{item.item_discount_amount}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div 
            className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50 transition-opacity duration-300" 
            onClick={onClose}
        >
            <div 
                className="relative bg-white p-8 rounded-lg shadow-xl w-11/12 max-w-5xl max-h-[90vh] overflow-y-auto transform scale-95 transition-transform duration-300" 
                onClick={e => e.stopPropagation()}
            >
                {/* Modal Header */}
                <div className="flex justify-between items-center pb-4 mb-4 border-b border-gray-200">
                    <h2 className="text-2xl font-semibold text-gray-800">Order Details - #{orderId}</h2>
                    <button 
                        className="text-gray-400 hover:text-gray-600 text-3xl leading-none font-semibold transition-colors duration-200" 
                        onClick={onClose}
                    >
                        &times;
                    </button>
                </div>

                {/* Modal Body */}
                {renderContent()}
            </div>
        </div>
    );
};

export default OrderDetailsModal;